import { CheckCircle2, Minus, Plus, ShoppingBag, X } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { formatCurrency } from "../lib/money";
import type {
  CartLine,
  DeliveryChoice,
  MysteryBucketService,
} from "../types";

interface CartDrawerProps {
  open: boolean;
  lines: CartLine[];
  delivery: DeliveryChoice | null;
  serviceCheckoutComplete?: boolean;
  onClose: () => void;
  onChangeQuantity: (key: string, delta: number) => void;
  onClear: () => void;
  onCheckoutComplete?: (service: MysteryBucketService | null) => void;
}

type CheckoutCompletionKind =
  | "standard"
  | "delivery"
  | "subscription"
  | "extras";

export function CartDrawer({
  open,
  lines,
  delivery,
  serviceCheckoutComplete = false,
  onClose,
  onChangeQuantity,
  onClear,
  onCheckoutComplete,
}: CartDrawerProps) {
  const [fulfilment, setFulfilment] = useState<"delivery" | "pickup">(
    "delivery",
  );
  const [completionKind, setCompletionKind] =
    useState<CheckoutCompletionKind | null>(null);
  const complete = completionKind !== null;
  const closeRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const reduceMotion = useReducedMotion();
  const total = lines.reduce(
    (sum, line) => sum + line.priceCents * line.quantity,
    0,
  );
  const serviceLine = lines.find((line) => line.service) ?? null;
  const mysteryPlan = serviceLine?.service ?? null;
  const isRecurringPlan =
    mysteryPlan?.cadence === "weekly" ||
    mysteryPlan?.cadence === "fortnightly";
  const recurringPlanStatus = isRecurringPlan
    ? mysteryPlan.lifecycleStatus
    : null;
  const recurringPerDropCents = isRecurringPlan
    ? serviceLine?.priceCents ?? 0
    : 0;
  const serviceLineCents = serviceLine
    ? serviceLine.priceCents * serviceLine.quantity
    : 0;
  const oneOffExtrasCents = Math.max(0, total - recurringPerDropCents);
  const serviceAlreadyCheckedOut =
    Boolean(mysteryPlan) &&
    (serviceCheckoutComplete ||
      (isRecurringPlan && recurringPlanStatus !== "in_cart"));
  const checkoutTotalCents = serviceAlreadyCheckedOut
    ? Math.max(0, total - serviceLineCents)
    : total;
  const checkoutHasItems = serviceAlreadyCheckedOut
    ? checkoutTotalCents > 0
    : lines.length > 0;
  const effectiveDelivery = mysteryPlan?.delivery ?? delivery;

  useEffect(() => {
    if (mysteryPlan) setFulfilment("delivery");
  }, [mysteryPlan]);

  useEffect(() => {
    if (!open) {
      setCompletionKind(null);
      return;
    }
    const previousOverflow = document.body.style.overflow;
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }

      if (event.key !== "Tab" || !drawerRef.current) return;

      const focusable = Array.from(
        drawerRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (!first || !last) {
        event.preventDefault();
        closeRef.current?.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
      previousFocusRef.current?.focus();
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.button
            className="cart-overlay"
            type="button"
            aria-label="Close cart"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduceMotion ? 0.12 : 0.2 }}
          />
          <motion.aside
            ref={drawerRef}
            className="cart-drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cart-title"
            data-reduced-motion={reduceMotion ? "true" : "false"}
            initial={
              reduceMotion
                ? { opacity: 0, x: 0 }
                : { opacity: 1, x: "100%" }
            }
            animate={{ opacity: 1, x: 0 }}
            exit={
              reduceMotion
                ? { opacity: 0, x: 0 }
                : { opacity: 1, x: "100%" }
            }
            transition={
              reduceMotion
                ? { duration: 0.12, ease: "linear" }
                : { type: "spring", damping: 30, stiffness: 290 }
            }
          >
            <header>
              <div className="cart-drawer__identity">
                <img
                  className="cart-drawer__logo"
                  src="/brand/sub-zero-logo-primary.png"
                  alt="Sub Zero Treats"
                />
                <div>
                  <p>Direct order concept</p>
                  <h2 id="cart-title">
                    {complete
                      ? "Preview complete"
                      : mysteryPlan
                        ? isRecurringPlan
                          ? "Your Mystery Bucket plan"
                          : "Your Mystery Bucket order"
                        : "Your treat run"}
                  </h2>
                </div>
              </div>
              <button
                ref={closeRef}
                type="button"
                className="icon-button"
                onClick={onClose}
                aria-label="Close cart"
              >
                <X aria-hidden="true" />
              </button>
            </header>

            {complete ? (
              <div className="checkout-complete">
                <CheckCircle2 aria-hidden="true" />
                <h3>
                  {completionKind === "subscription"
                    ? "Your subscription preview is ready."
                    : completionKind === "delivery"
                      ? "Your delivery preview is ready."
                      : completionKind === "extras"
                        ? "Your extra treats preview is ready."
                        : "Your direct-order preview is ready."}
                </h3>
                <p>
                  {completionKind === "subscription" && mysteryPlan
                    ? `This previews the ${mysteryPlan.cadence} cadence, first-drop date and per-drop price. No payment, order, subscription or delivery booking was created.`
                    : completionKind === "delivery"
                      ? "This demonstrates one Mystery Bucket, its selected delivery run and complete first-drop price. No payment, order or delivery booking was created."
                      : completionKind === "extras"
                        ? "This demonstrates a separate one-off checkout for treats added after the Mystery Bucket preview. No payment, order or delivery booking was created."
                        : "This concept stops before payment. In a live build, Sub Zero would choose the checkout, payment and fulfilment setup before launch."}
                </p>
                <button className="button button--pink" type="button" onClick={onClose}>
                  Back to the treats
                </button>
              </div>
            ) : (
              <>
                <div className="cart-drawer__body">
                  {lines.length === 0 ? (
                    <div className="empty-cart">
                      <ShoppingBag aria-hidden="true" />
                      <h3>Your treat run starts here.</h3>
                      <p>Add a favourite or build a custom mix.</p>
                    </div>
                  ) : (
                    <>
                      <button className="clear-cart" type="button" onClick={onClear}>
                        Clear order
                      </button>
                      <ul className="cart-lines">
                        {lines.map((line) => (
                          <li key={line.key}>
                            {line.image && (
                              <div className="cart-lines__media" aria-hidden="true">
                                <img
                                  className="cart-lines__image"
                                  src={line.image}
                                  alt=""
                                />
                                {line.image ===
                                  "/products/pick-mix-higgsfield.webp" && (
                                  <img
                                    className="cart-lines__bucket-logo"
                                    src="/brand/sub-zero-logo-primary.png"
                                    alt=""
                                  />
                                )}
                              </div>
                            )}
                            <div>
                              <h3>{line.name}</h3>
                              {line.detail && <p>{line.detail}</p>}
                              {line.service ? (
                                <>
                                  <dl className="cart-line-service">
                                    <div>
                                      <dt>Bucket</dt>
                                      <dd>
                                        {formatCurrency(
                                          line.service.bucketSubtotalCents,
                                        )}
                                      </dd>
                                    </div>
                                    <div>
                                      <dt>Local delivery</dt>
                                      <dd>
                                        {formatCurrency(
                                          line.service.deliveryFeeCents,
                                        )}
                                      </dd>
                                    </div>
                                  </dl>
                                   <span className="cart-line-service__cadence">
                                     {line.service.cadence === "once"
                                       ? "One-off delivery"
                                       : line.service.cadence === "weekly"
                                         ? "Every week"
                                         : "Every fortnight"}{" "}
                                     &middot; quantity fixed
                                   </span>
                                  {isRecurringPlan && (
                                    <span className="cart-line-service__status">
                                      {recurringPlanStatus === "in_cart"
                                        ? "Awaiting demo checkout"
                                        : recurringPlanStatus === "active"
                                          ? "Active account preview"
                                          : recurringPlanStatus === "paused"
                                            ? "Deliveries paused"
                                            : "Plan cancelled"}
                                    </span>
                                  )}
                                </>
                              ) : (
                                <>
                                  <strong>{formatCurrency(line.priceCents)}</strong>
                                  <div className="quantity-control">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        onChangeQuantity(line.key, -1)
                                      }
                                      aria-label={`Decrease ${line.name} quantity`}
                                    >
                                      <Minus aria-hidden="true" />
                                    </button>
                                    <span aria-live="polite">
                                      {line.quantity}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        onChangeQuantity(line.key, 1)
                                      }
                                      aria-label={`Increase ${line.name} quantity`}
                                    >
                                      <Plus aria-hidden="true" />
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                            <strong>
                              {formatCurrency(line.priceCents * line.quantity)}
                              {line.service && (
                                <small>
                                  {line.service.cadence === "once"
                                    ? "this drop"
                                    : "per delivery"}
                                </small>
                              )}
                            </strong>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>

                <footer>
                  <div className="fulfilment-toggle" role="group" aria-label="Fulfilment">
                    <button
                      type="button"
                      className={fulfilment === "delivery" ? "is-active" : ""}
                      onClick={() => setFulfilment("delivery")}
                      aria-pressed={fulfilment === "delivery"}
                    >
                      Delivery
                    </button>
                    <button
                      type="button"
                      className={fulfilment === "pickup" ? "is-active" : ""}
                      onClick={() => setFulfilment("pickup")}
                      aria-pressed={fulfilment === "pickup"}
                      disabled={Boolean(mysteryPlan)}
                      aria-describedby={
                        mysteryPlan ? "mystery-plan-delivery-only" : undefined
                      }
                    >
                      Pickup
                    </button>
                  </div>
                  {mysteryPlan && (
                    <p id="mystery-plan-delivery-only" className="cart-service-note">
                      Mystery Bucket plans use the selected local delivery run.
                    </p>
                  )}
                  {fulfilment === "delivery" && (
                    <p className="cart-delivery">
                      {effectiveDelivery
                        ? `${isRecurringPlan && recurringPlanStatus !== "in_cart" ? "Next plan drop" : isRecurringPlan ? "First plan drop" : mysteryPlan ? "Selected delivery" : "Proposed run"}: ${effectiveDelivery.day} ${effectiveDelivery.date}, ${effectiveDelivery.window}`
                        : (
                          <>
                            Choose a proposed delivery day to preview the next
                            step.{" "}
                            <a href="#delivery-days" onClick={onClose}>
                              Choose a day
                            </a>
                          </>
                        )}
                    </p>
                  )}
                  <p className="demo-note">
                    Preview only &mdash; no order or payment is created.
                  </p>
                  {isRecurringPlan && !serviceAlreadyCheckedOut && (
                    <div className="cart-recurring-summary">
                      <span>
                        First checkout
                        <strong>{formatCurrency(total)}</strong>
                      </span>
                      <span>
                        Then each Mystery Bucket drop
                        <strong>{formatCurrency(recurringPerDropCents)}</strong>
                      </span>
                      {oneOffExtrasCents > 0 && (
                        <small>
                          {formatCurrency(oneOffExtrasCents)} of extras appears
                          only in the first checkout and does not repeat.
                        </small>
                      )}
                    </div>
                  )}
                  {serviceAlreadyCheckedOut && (
                    <div className="cart-recurring-summary">
                      <span>
                        {isRecurringPlan
                          ? "Existing Mystery Bucket plan"
                          : "Completed one-off bucket"}
                        <strong>{formatCurrency(serviceLineCents)}</strong>
                      </span>
                      {checkoutTotalCents > 0 && (
                        <span>
                          New one-off treats
                          <strong>{formatCurrency(checkoutTotalCents)}</strong>
                        </span>
                      )}
                      <small>
                        {isRecurringPlan
                          ? "The plan is not charged again in this extras checkout."
                          : "The completed bucket is not charged again in this extras checkout."}
                      </small>
                    </div>
                  )}
                  {!serviceAlreadyCheckedOut || checkoutHasItems ? (
                    <button
                      type="button"
                      className="button button--pink checkout-button"
                      disabled={
                        !checkoutHasItems ||
                        (fulfilment === "delivery" && !effectiveDelivery)
                      }
                      onClick={() => {
                        const nextCompletionKind: CheckoutCompletionKind =
                          serviceAlreadyCheckedOut
                            ? "extras"
                            : isRecurringPlan
                              ? "subscription"
                              : mysteryPlan
                                ? "delivery"
                                : "standard";
                        setCompletionKind(nextCompletionKind);
                        onCheckoutComplete?.(
                          nextCompletionKind === "extras" ? null : mysteryPlan,
                        );
                      }}
                    >
                      {serviceAlreadyCheckedOut
                        ? "Preview extras checkout"
                        : mysteryPlan
                        ? isRecurringPlan
                          ? "Preview subscription checkout"
                          : "Preview delivery checkout"
                        : "Preview checkout"}
                      <span>
                        {formatCurrency(checkoutTotalCents)}
                        {isRecurringPlan && !serviceAlreadyCheckedOut && (
                          <small> first checkout</small>
                        )}
                      </span>
                    </button>
                  ) : (
                    <p className="cart-service-note" role="status">
                      {!isRecurringPlan
                        ? "This one-off Mystery Bucket checkout is already complete in the demo. Add another treat to preview a separate extras checkout."
                        : recurringPlanStatus === "paused"
                        ? "This plan is paused. Resume it in the account preview before another drop can proceed."
                        : recurringPlanStatus === "cancelled"
                          ? "This plan is cancelled. No future checkout is available."
                          : "This subscription checkout is already complete in the demo. Use the account preview to manage its next drop."}
                    </p>
                  )}
                </footer>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
